import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { TrendingDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(n || 0);
const fmM = (m) => { if (!m) return '—'; const [y, mo] = m.split('-'); return `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+mo]} ${y}`; };

const Outcome = ({ label, sim, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs text-white font-bold">{label}</p>
    {sim?.paid_off
      ? <p className="text-emerald-300 font-black text-lg">{fmM(sim.debt_free_month)} <span className="text-xs font-normal text-slate-300">({sim.months} months · {R(sim.total_interest)} interest)</span></p>
      : <p className="text-rose-300 font-bold text-sm">Not paid off in 30 years — payments don't cover interest</p>}
  </div>
);

export const DebtPayoffChart = ({ headers, refreshKey }) => {
  const [extra, setExtra] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API_URL}/api/debts/projection`, { headers, params: { extra: Number(extra) || 0 } })).data); }
    catch { setData(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra, refreshKey]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const map = {};
    data.history.forEach((p) => { map[p.month] = { month: p.month, actual: p.total }; });
    const now = data.history[data.history.length - 1];
    map[now.month] = { ...map[now.month], plan: now.total, extra: data.with_extra ? now.total : undefined };
    data.baseline.points.forEach((p) => { map[p.month] = { ...(map[p.month] || { month: p.month }), plan: p.total }; });
    (data.with_extra?.points || []).forEach((p) => { map[p.month] = { ...(map[p.month] || { month: p.month }), extra: p.total }; });
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);

  if (!data || data.current_total <= 0) return null;
  const saved = data.with_extra?.paid_off && data.baseline.paid_off ? data.baseline.months - data.with_extra.months : null;

  return (
    <Card className="bg-slate-800 border-slate-600" data-testid="debt-payoff-chart">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-white flex items-center gap-2 text-base"><TrendingDown className="h-5 w-5 text-emerald-400" /> Debt Payoff Plan</CardTitle>
        <label className="flex items-center gap-2 text-sm text-white">
          What if we pay an extra
          <Input type="number" min="0" placeholder="R 0" value={extra} onChange={(e) => setExtra(e.target.value)} className="w-32 h-8 bg-slate-700 border-slate-600" data-testid="debt-extra-input" />
          per month?
        </label>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <Outcome label={`DEBT-FREE AT CURRENT PAYMENTS (${R(data.monthly_payments)}/month)`} sim={data.baseline} testId="debt-free-baseline" />
          {data.with_extra && <Outcome label={`WITH EXTRA ${R(data.extra)}/month`} sim={data.with_extra} testId="debt-free-extra" />}
          {saved > 0 && <div data-testid="debt-months-saved"><p className="text-xs text-white font-bold">YOU SAVE</p><p className="text-yellow-300 font-black text-lg">{saved} months · {R(data.baseline.total_interest - data.with_extra.total_interest)} interest</p></div>}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ left: 10, right: 10, bottom: 10 }}>
              <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={fmM} stroke="#cbd5e1" fontSize={11} minTickGap={24} />
              <YAxis tickFormatter={(v) => `R${Math.round(v / 1000)}k`} stroke="#cbd5e1" fontSize={11} width={60} />
              <Tooltip formatter={(v) => R(v)} labelFormatter={fmM} contentStyle={{ background: '#0f172a', border: '1px solid #475569', color: '#fff' }} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <ReferenceLine x={data.history[data.history.length - 1].month} stroke="#facc15" strokeDasharray="4 4" label={{ value: 'Today', fill: '#facc15', fontSize: 11 }} />
              <Line type="monotone" dataKey="actual" name="Actual" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="plan" name="Current payments" stroke="#38bdf8" strokeWidth={2} dot={false} connectNulls />
              {data.with_extra && <Line type="monotone" dataKey="extra" name="With extra" stroke="#22c55e" strokeWidth={2} dot={false} connectNulls />}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-slate-300">Projection adds monthly interest to each balance, pays each debt's monthly payment, and puts any extra (plus payments freed up from settled debts) on the highest-interest debt first.</p>
      </CardContent>
    </Card>
  );
};
